import React, { useEffect, useState } from 'react';
import type { PMCConfig } from '@kyruus/types';
import { useSelector } from 'react-redux';
import { Box } from '@biome-design-system/ui/Box';

import type {
  SearchModification,
  SearchFacetWithAppliedTerms,
  SearchSummary,
  Log
} from 'Src/types';
import { isModuleEnabled, MODULES } from 'Common/config';
import { configSelector } from 'Src/behaviors/configuration';
import { LOCATION_FACET_DEFAULT_DISTANCE } from 'Src/utils/constants';

import { messages } from '../../messages';
import { Distance } from './Distance';
import { LocationInput } from './LocationInput';
import { CurrentLocationButton } from './CurrentLocationButton';

export const DISTANCE_ANY = 'Any';

export interface LocationFilterProps {
  locationFacet: SearchFacetWithAppliedTerms;
  searchSummary: SearchSummary;
  updateSearch: (modifications: SearchModification[]) => void;
  log: Log;
}

export const LocationFilter = ({
  locationFacet,
  searchSummary,
  updateSearch,
  log
}: LocationFilterProps) => {
  const config: PMCConfig = useSelector(configSelector);
  const defaultDistance = searchSummary.location
    ? DISTANCE_ANY
    : locationFacet.default_distance;

  const [searchParams, setSearchParams] = useState<
    Pick<SearchSummary, 'location' | 'display_location' | 'distance'>
  >({
    location: searchSummary.location || '',
    display_location:
      searchSummary.display_location || searchSummary.location || '',
    distance: searchSummary.distance ?? defaultDistance
  });

  useEffect(() => {
    setSearchParams({
      location: searchSummary.location || '',
      display_location:
        searchSummary.display_location || searchSummary.location || '',
      distance: searchSummary.distance ?? defaultDistance
    });
  }, [
    searchSummary.location,
    searchSummary.display_location,
    searchSummary.distance,
    /** _original_input_distance is needed here to trigger the useEffect
     * when the searchSummary.distance remains constant due to Search V9's auto distance expansion feature */
    searchSummary._original_input_distance,
    defaultDistance
  ]);

  /**
   * Modifies the route history with the current filters and applies them to update the search results
   */
  async function applyFilter({
    location,
    display_location,
    distance
  }: Pick<SearchSummary, 'location' | 'display_location' | 'distance'>) {
    if (!location) {
      return;
    }

    const modifications: SearchModification[] = [
      {
        action: 'append',
        key: 'location',
        value: location
      },
      {
        action: 'append',
        key: 'display_location',
        value: display_location
      }
    ];

    /** Previously using Search V8, a distance of 0 would return results as if no distance filter was applied (appropriate for any "Any miles" search).
     * However, now in Search V9, a distance of 0 returns results only if they exist at specific location, otherwise the API will auto expand the distance
     * (response's messages.distance_expansion) in specific mile increments until it finds results to return.  */
    if (distance === DISTANCE_ANY) {
      modifications.push({ action: 'delete_key', key: 'distance' });
    } else {
      modifications.push({
        action: 'append',
        key: 'distance',
        value: distance
      });
    }
    updateSearch(modifications);
  }

  const useCurrentLocationEnabled =
    !isModuleEnabled(config, MODULES.CRM_INTEGRATION) &&
    Boolean(locationFacet?.enable_use_current_location);

  const handleUseCurrentLocation = (
    location: string,
    displayLocation: string
  ) => {
    const defaultDistance =
      locationFacet.default_distance ?? LOCATION_FACET_DEFAULT_DISTANCE;
    const params = {
      location: location,
      display_location: displayLocation,
      distance: distance == null ? defaultDistance : distance
    };
    setSearchParams(params);
    applyFilter(params);
    log('user_action.search_results.distance_facet.use_current_location');
  };

  const handleLocationChange = (selectedItem?: string) => {
    const params = selectedItem
      ? {
          distance,
          location: selectedItem,
          display_location: selectedItem
        }
      : searchParams;
    setSearchParams(params);
    applyFilter(params);
    log('user_action.search_results.distance_facet.search_location');
  };

  const { location, display_location, distance } = searchParams;
  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Distance
        distance={distance}
        distanceOptions={locationFacet.distance_options || []}
        onChange={(distance) => {
          setSearchParams({
            location,
            display_location,
            distance
          });
          log(
            'user_action.search_results.distance_facet.within_distance_selected'
          );
        }}
      />
      <LocationInput
        distance={distance}
        displayLocation={display_location}
        onChange={handleLocationChange}
        onInputValueChange={(inputValue: string) => {
          setSearchParams({
            distance,
            location: inputValue,
            display_location: inputValue
          });
        }}
        onSubmit={handleLocationChange}
        placeholder={messages.placeholder}
      />

      {useCurrentLocationEnabled && (
        <CurrentLocationButton
          displayLocation={display_location}
          onUseCurrentLocation={handleUseCurrentLocation}
        />
      )}
    </Box>
  );
};
